import React, { useEffect, useState } from "react";
import {
  Box, Button, Flex, HStack, Input, InputGroup, InputLeftElement, Select, Spinner, Table, Thead, Tbody,
  Tr, Th, Td, Text, Tooltip,
} from "@chakra-ui/react";
import { Search2Icon, ViewIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { CustomToast } from "../../components";
import { useFetch } from "../../hooks/useFetch";
import * as routesNames from "../../constant/routes";
import { inr } from "./customerDetail";
import { cardProps, thProps } from "./billingui";

export interface CustomerProjectItem {
  id: number;
  projectId?: number;
  projectName: string;
  projectAmount?: number | null;
  isActive?: boolean;
  hasBilling?: boolean;
  leadId?: number;
  projectStartDate?: string | null;
  projectEndDate?: string | null;
  projectEstimatedDuration?: number | null;
  serviceType?: number | null;
}

interface CustomerProjectListProps {
  customerId: number;
  fallbackProjects?: any[];
  selectedCompanyId?: number | null;
  selectedPocId?: number | null;
  companyId?: number | null;
  pocId?: number | null;
}

export const CustomerProjectList: React.FC<CustomerProjectListProps> = ({
  customerId,
  fallbackProjects,
  selectedCompanyId,
  selectedPocId,
  companyId,
  pocId,
}) => {
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);
  const navigate = useNavigate();

  const actualCompanyId = selectedCompanyId ?? companyId;
  const actualPocId = selectedPocId ?? pocId;

  const [projects, setProjects] = useState<CustomerProjectItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>("");
  const [billingFilter, setBillingFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const loadProjects = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (customerId) queryParams.append("customerId", String(customerId));
      if (actualCompanyId !== undefined && actualCompanyId !== null) {
        queryParams.append("companyId", String(actualCompanyId));
      }
      if (actualPocId !== undefined && actualPocId !== null ) {
        queryParams.append("pocId", String(actualPocId));
      }

      const queryString = queryParams.toString();
      const endpoint = `Customers/ProjectDetails${queryString ? `?${queryString}` : ""}`;

      const response = await fetchApi<any>(
        endpoint, "GET", null, null, "");
      if (response) {
        const list = Array.isArray(response)
          ? response
          : response.items || response.data || response.projects || [];
        setProjects(list);
      } else if (fallbackProjects && fallbackProjects.length > 0) {
        setProjects(fallbackProjects);
      } else {
        setProjects([]);
      }
    } catch (error) {
      console.error("Error loading customer project list:", error);
      if (fallbackProjects && fallbackProjects.length > 0) {
        setProjects(fallbackProjects);
      } else {
        setProjects([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (customerId) {
      loadProjects();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, actualCompanyId, actualPocId]);

  // Navigate to Project Details with context
  const handleNavigateProject = (project: CustomerProjectItem, openBilling: boolean = false) => {
    const projId = project.projectId || project.id;
    navigate(routesNames.PROJECTDETAIL, {
      state: {
        fullScreen: true,
        from: `/customers/${customerId}`,
        fromLabel: "Customer Details",
        initialTab: openBilling ? "billing" : undefined,
        lead: {
          projectId: projId,
          id: projId,
          companyId: actualCompanyId,
          company_Id: actualCompanyId,
          pocId: actualPocId,
          projectName: project.projectName,
          projectAmount: project.projectAmount,
          leadId: project.leadId,
          projectEstimatedDuration: project.projectEstimatedDuration,
          projectStartDate: project.projectStartDate,
          projectEndDate: project.projectEndDate,
          serviceType: project.serviceType,
          isActive: project.isActive,
        },
      },
    });
  };

  // Filtered project list
  const filteredProjects = projects.filter((p) => {
    const matchesSearch =
      !search ||
      p.projectName.toLowerCase().includes(search.toLowerCase());

    const matchesBilling =
      billingFilter === "all" ||
      (billingFilter === "configured" && p.hasBilling) ||
      (billingFilter === "not_configured" && !p.hasBilling);

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && (p.isActive ?? true)) ||
      (statusFilter === "inactive" && !(p.isActive ?? true));

    return matchesSearch && matchesBilling && matchesStatus;
  });

  return (
    <Box {...cardProps} p={5}>
      {/* Search and Filters Header */}
      <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={3} >
        <HStack spacing={3} wrap="wrap">
          <InputGroup width="260px" size="sm">
            <InputLeftElement pointerEvents="none">
              <Search2Icon color="gray.500" />
            </InputLeftElement>
            <Input placeholder="Search project..." value={search} onChange={(e) => setSearch(e.target.value)} bg="white" borderRadius="md" />
          </InputGroup>

          <Select
            size="sm"
            width="170px"
            borderRadius="md"
            value={billingFilter}
            onChange={(e) => setBillingFilter(e.target.value)}
            bg="white"
          >
            <option value="all">All Billing</option>
            <option value="configured">Configured</option>
            <option value="not_configured">Not Configured</option>
          </Select>

          <Select
            size="sm"
            width="140px"
            borderRadius="md"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            bg="white"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>
        </HStack>

        <Text fontSize="sm" color="gray.600" fontWeight="500">
          Total Projects: {filteredProjects.length}
        </Text>
      </Flex>

      {/* Projects Table */}
      <Box overflowX="auto" borderRadius="md" border="1px solid #EDEEEE">
        <Table size="sm" variant="simple">
          <Thead bg="#F9FAFB">
            <Tr>
              <Th {...thProps} width="60px">
                S.No
              </Th>
              <Th {...thProps}>Project Name</Th>
              <Th {...thProps} isNumeric>
                Amount
              </Th>
              <Th {...thProps}>Project Status</Th>
              <Th {...thProps}>Billing</Th>
              <Th {...thProps} textAlign="right">
                Action
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={6} textAlign="center" py={10}>
                  <Spinner size="md" color="blue.500" />
                </Td>
              </Tr>
            ) : filteredProjects.length === 0 ? (
              <Tr>
                <Td colSpan={6} textAlign="center" py={10}>
                  <Text color="gray.500" fontSize="sm" fontWeight="500">
                    No Projects Found
                  </Text>
                </Td>
              </Tr>
            ) : (
              filteredProjects.map((p, index) => {
                const isActive = p.isActive ?? true;
                return (
                  <Tr
                    key={p.id}
                    _hover={{ bg: "#F9FAFB" }}
                    cursor="pointer"
                    onClick={() => handleNavigateProject(p)}
                  >
                    {/* S.No */}
                    <Td fontSize="sm" color="gray.700" width="60px">
                      {index + 1}
                    </Td>

                    {/* Project Name */}
                    <Td>
                      <Tooltip label={p.projectName} hasArrow placement="top">
                        <Box
                          color="#2563EB"
                          fontWeight="600"
                          maxW="260px"
                          isTruncated
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNavigateProject(p);
                          }}
                        >
                          {p.projectName}
                        </Box>
                      </Tooltip>
                    </Td>

                    {/* Amount */}
                    <Td isNumeric fontWeight="600" color="#1D2939">
                      {inr(p.projectAmount)}
                    </Td>

                    {/* Status Badge */}
                    <Td>
                      <Box
                        display="inline-flex"
                        alignItems="center"
                        bg={isActive ? "#DCFCE7" : "#FEE2E2"}
                        color={isActive ? "#15803D" : "#DC2626"}
                        px="10px"
                        py="3px"
                        borderRadius="16px"
                        fontSize="12px"
                        fontWeight="600"
                      >
                        {isActive ? "Active" : "Inactive"}
                      </Box>
                    </Td>

                    {/* Billing Status Badge */}
                    <Td>
                      <Box
                        display="inline-flex"
                        alignItems="center"
                        bg={p.hasBilling ? "#DCFCE7" : "#FEF0C7"}
                        color={p.hasBilling ? "#15803D" : "#B54708"}
                        px="10px"
                        py="3px"
                        borderRadius="16px"
                        fontSize="12px"
                        fontWeight="600"
                      >
                        {p.hasBilling ? "Configured" : "Not configured"}
                      </Box>
                    </Td>

                    {/* Action */}
                    <Td textAlign="right">
                      <HStack justify="flex-end" spacing={2}>
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="blue"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNavigateProject(p, true);
                          }}
                        >
                          Billing
                        </Button>

                        <Tooltip label="View Project Details">
                          <Flex
                            align="center"
                            justify="center"
                            w="28px"
                            h="28px"
                            borderRadius="full"
                            bg="purple.50"
                            cursor="pointer"
                            _hover={{ bg: "purple.100" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNavigateProject(p, true);
                            }}
                          >
                            <ViewIcon color="purple.500" boxSize={3.5} />
                          </Flex>
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                );
              })
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default CustomerProjectList;
